import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

interface LegalDialogsProps {
  termsOpen: boolean;
  setTermsOpen: (open: boolean) => void;
  privacyPolicyOpen: boolean;
  setPrivacyPolicyOpen: (open: boolean) => void;
  language?: string;
  openFullTerms?: () => void;
  openFullPrivacy?: () => void;
}

export const LegalDialogs = ({
  termsOpen, 
  setTermsOpen, 
  privacyPolicyOpen, 
  setPrivacyPolicyOpen,
  language = "fi",
  openFullTerms,
  openFullPrivacy
}: LegalDialogsProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const getTexts = () => {
    if (language === "sv") {
      return {
        termsTitle: "Användarvillkor",
        termsDescription: "Lainasampo.fi tjänstens användarvillkor",
        section1Title: "1. Allmänt",
        section1Text: "Dessa användarvillkor gäller för användningen av Lainasampo.fi webbplats och dess tjänster. Genom att använda webbplatsen godkänner du dessa villkor.",
        section2Title: "2. Tjänstebeskrivning",
        section2Text: "Lainasampo.fi är en jämförelsetjänst för lån som hjälper användare att hitta det mest lämpliga lånerbjudandet på marknaden. Webbplatsen förmedlar den information som användaren lämnar till samarbetspartners som erbjuder lånetjänster.",
        section3Title: "3. Databehandling",
        section3Text: "Användarens information används endast för att begära lånerbjudanden från samarbetspartners. Informationsbehandlingen följer de praxis som definieras i integritetspolicyn.",
        section4Title: "4. Ansvar",
        section4Text: "Lainasampo.fi är inte en långivare och ansvarar inte för lån som erbjuds av samarbetspartners eller deras villkor. Ansvaret för låneavtalet ligger alltid hos användaren och långivaren.",
        readFullTerms: "Läs de fullständiga användarvillkoren",
        close: "Stäng",
        privacyTitle: "Integritetspolicy",
        privacyDescription: "Lainasampo.fi tjänstens integritetspolicy",
        privacy1Title: "1. Personuppgiftsansvarig",
        privacy1Text: "Pulaton Oy ansvarar för integriteten för Lainasampo.fi-tjänsten.",
        privacy2Title: "2. Insamlad information",
        privacy2Text: "Vi samlar in följande information om användare för behandling av lånerbjudanden: namn, e-postadress, telefonnummer, anställningsinformation och låninformation.",
        privacy3Title: "3. Informationsanvändning",
        privacy3Text: "Informationen används för att begära lånerbjudanden från samarbetspartners och för att förbättra tjänstens kvalitet.",
        privacy4Title: "4. Utlämnande av information",
        privacy4Text: "Information lämnas endast ut till samarbetspartners för att göra ett lånerbjudande. Information lämnas inte ut för marknadsföringsändamål utan användarens samtycke.",
        privacy5Title: "5. Datalagring",
        privacy5Text: "Information lagras så länge som är nödvändigt för att tillhandahålla tjänsten eller uppfylla lagstadgade skyldigheter.",
        readFullPrivacy: "Läs den fullständiga integritetspolicyn"
      };
    } else if (language === "en") {
      return {
        termsTitle: "Terms of Service",
        termsDescription: "Lainasampo.fi service terms and conditions",
        section1Title: "1. General",
        section1Text: "These terms of service govern the use of the Lainasampo.fi website and its services. By using the site, you accept these terms.",
        section2Title: "2. Service Description",
        section2Text: "Lainasampo.fi is a loan comparison service that helps users find the most suitable loan offer in the market. The site forwards the information provided by the user to partners offering loan services.",
        section3Title: "3. Data Processing",
        section3Text: "User information is used only for requesting loan offers from partners. Data processing follows the practices defined in the privacy policy.",
        section4Title: "4. Responsibilities",
        section4Text: "Lainasampo.fi is not a lender and is not responsible for loans offered by partners or their terms. Responsibility for the loan agreement always lies with the user and the lender.",
        readFullTerms: "Read the full Terms of Service",
        close: "Close",
        privacyTitle: "Privacy Policy",
        privacyDescription: "Lainasampo.fi service privacy practices",
        privacy1Title: "1. Data Controller",
        privacy1Text: "Pulaton Oy is responsible for the privacy of the Lainasampo.fi service.",
        privacy2Title: "2. Collected Information",
        privacy2Text: "We collect the following information from users for loan offer processing: name, email address, phone number, employment information, and loan information.",
        privacy3Title: "3. Information Usage",
        privacy3Text: "Information is used for requesting loan offers from partners and improving service quality.",
        privacy4Title: "4. Information Disclosure",
        privacy4Text: "Information is only disclosed to partners for making a loan offer. Information is not disclosed for marketing purposes without user consent.",
        privacy5Title: "5. Data Storage",
        privacy5Text: "Information is stored as long as necessary for providing the service or fulfilling statutory obligations.",
        readFullPrivacy: "Read the full Privacy Policy"
      };
    } else {
      return {
        termsTitle: "Käyttöehdot",
        termsDescription: "Lainasampo.fi palvelun käyttöehdot",
        section1Title: "1. Yleistä",
        section1Text: "Nämä käyttöehdot koskevat Lainasampo.fi -verkkosivuston ja sen palveluiden käyttöä. Käyttämällä sivustoa hyväksyt nämä ehdot.",
        section2Title: "2. Palvelun kuvaus",
        section2Text: "Lainasampo.fi on lainojen vertailupalvelu, joka auttaa käyttäjiä löytämään sopivimman lainatarjouksen markkinoilta. Sivusto välittää käyttäjän antamat tiedot lainapalveluita tarjoaville yhteistyökumppaneille.",
        section3Title: "3. Tietojen käsittely",
        section3Text: "Käyttäjän antamia tietoja käytetään ainoastaan lainatarjousten pyytämiseen yhteistyökumppaneilta. Tietojen käsittelyssä noudatetaan tietosuojaselosteessa määriteltyjä käytäntöjä.",
        section4Title: "4. Vastuut",
        section4Text: "Lainasampo.fi ei ole lainanantaja eikä vastaa yhteistyökumppaneiden tarjoamista lainoista tai niiden ehdoista. Vastuu lainasopimuksesta on aina käyttäjällä ja lainanantajalla.",
        readFullTerms: "Lue täydelliset käyttöehdot",
        close: "Sulje",
        privacyTitle: "Tietosuojaseloste",
        privacyDescription: "Lainasampo.fi palvelun tietosuojakäytännöt",
        privacy1Title: "1. Rekisterinpitäjä",
        privacy1Text: "Lainasampo.fi -palvelun tietosuojasta vastaa Pulaton Oy.",
        privacy2Title: "2. Kerättävät tiedot",
        privacy2Text: "Keräämme käyttäjistä seuraavia tietoja lainatarjousten käsittelyä varten: nimi, sähköpostiosoite, puhelinnumero, työllisyystiedot ja lainaan liittyvät tiedot.",
        privacy3Title: "3. Tietojen käyttötarkoitus",
        privacy3Text: "Tietoja käytetään lainatarjousten pyytämiseen yhteistyökumppaneilta ja palvelun laadun parantamiseen.",
        privacy4Title: "4. Tietojen luovutus",
        privacy4Text: "Tietoja luovutetaan ainoastaan yhteistyökumppaneille lainatarjouksen tekemistä varten. Tietoja ei luovuteta markkinointitarkoituksiin ilman käyttäjän suostumusta.",
        privacy5Title: "5. Tietojen säilytys",
        privacy5Text: "Tietoja säilytetään niin kauan kuin on tarpeen palvelun tarjoamiseksi tai lakisääteisten velvoitteiden täyttämiseksi.",
        readFullPrivacy: "Lue täydellinen tietosuojaseloste"
      };
    }
  };

  const texts = getTexts();

  const handleFullTermsClick = () => {
    setTermsOpen(false);
    // Use openFullTerms if provided, otherwise navigate directly
    if (openFullTerms) {
      openFullTerms();
    } else {
      // Navigate to terms page based on language
      if (language === "en") {
        navigate("/en/terms");
      } else if (language === "sv") {
        navigate("/sv/terms");
      } else if (language === "ar") {
        navigate("/ar/terms");
      } else if (language === "he") {
        navigate("/he/terms");
      } else {
        navigate("/kayttoehdot");
      }
    }
  };

  const handleFullPrivacyClick = () => {
    setPrivacyPolicyOpen(false);
    // Use openFullPrivacy if provided, otherwise navigate directly
    if (openFullPrivacy) {
      openFullPrivacy();
    } else {
      // Navigate to privacy policy page based on language
      if (language === "en") {
        navigate("/en/privacy");
      } else if (language === "sv") {
        navigate("/sv/privacy");
      } else if (language === "ar") {
        navigate("/ar/privacy");
      } else if (language === "he") {
        navigate("/he/privacy");
      } else {
        navigate("/tietosuojaseloste");
      }
    }
  };

  return (
    <>
      {/* Käyttöehdot Dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] md:max-w-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden p-4 md:p-6 safe-area-inset">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl">{texts.termsTitle}</DialogTitle>
            <DialogDescription className="text-base">
              {texts.termsDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-base space-y-4">
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.section1Title}</h3>
              <p className="leading-relaxed">{texts.section1Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.section2Title}</h3>
              <p className="leading-relaxed">{texts.section2Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.section3Title}</h3>
              <p className="leading-relaxed">{texts.section3Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.section4Title}</h3>
              <p className="leading-relaxed">{texts.section4Text}</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-6 md:flex-row md:justify-between">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleFullTermsClick}
              className="w-full md:w-auto min-h-[44px] text-base"
            >
              {texts.readFullTerms}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setTermsOpen(false)}
              className="w-full md:w-auto min-h-[44px] text-base"
            >
              {texts.close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tietosuojaseloste Dialog */}
      <Dialog open={privacyPolicyOpen} onOpenChange={setPrivacyPolicyOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] md:max-w-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden p-4 md:p-6 safe-area-inset">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl">{texts.privacyTitle}</DialogTitle>
            <DialogDescription className="text-base">
              {texts.privacyDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-base space-y-4">
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.privacy1Title}</h3>
              <p className="leading-relaxed">{texts.privacy1Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.privacy2Title}</h3>
              <p className="leading-relaxed">{texts.privacy2Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.privacy3Title}</h3>
              <p className="leading-relaxed">{texts.privacy3Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.privacy4Title}</h3>
              <p className="leading-relaxed">{texts.privacy4Text}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">{texts.privacy5Title}</h3>
              <p className="leading-relaxed">{texts.privacy5Text}</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-6 md:flex-row md:justify-between">
            <Button 
              type="button" 
              variant="outline"
              onClick={handleFullPrivacyClick}
              className="w-full md:w-auto min-h-[44px] text-base"
            >
              {texts.readFullPrivacy}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setPrivacyPolicyOpen(false)}
              className="w-full md:w-auto min-h-[44px] text-base"
            >
              {texts.close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
